import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2, X, FileText, Hash, Type, Layers, FolderTree, DollarSign, AlignLeft, Building2, TrendingUp, TrendingDown, Wallet, CreditCard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../hooks/useCurrency';

const ChartOfAccounts = () => {
  const { token } = useAuth();
  const { formatCurrency } = useCurrency();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [formData, setFormData] = useState({
    account_code: '',
    account_name: '',
    account_type: 'Asset',
    account_category: '',
    parent_account_id: '',
    description: '',
    opening_balance: 0
  });

  useEffect(() => {
    fetchAccounts();
  }, [filterType]);

  const fetchAccounts = async () => {
    try {
      const params = filterType ? { type: filterType } : {};
      const response = await axios.get('/api/chart-of-accounts', { params });
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        opening_balance: parseFloat(formData.opening_balance) || 0,
        parent_account_id: formData.parent_account_id || null
      };
      
      if (editingAccount) {
        await axios.put(`/api/chart-of-accounts/${editingAccount.id}`, payload);
        toast.success('Account updated successfully');
      } else {
        await axios.post('/api/chart-of-accounts', payload);
        toast.success('Account created successfully');
      }
      fetchAccounts();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      account_code: account.account_code,
      account_name: account.account_name,
      account_type: account.account_type,
      account_category: account.account_category || '',
      parent_account_id: account.parent_account_id || '',
      description: account.description || '',
      opening_balance: account.opening_balance || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await axios.delete(`/api/chart-of-accounts/${id}`);
        toast.success('Account deleted successfully');
        fetchAccounts();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete account');
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAccount(null);
    setFormData({
      account_code: '',
      account_name: '',
      account_type: 'Asset',
      account_category: '',
      parent_account_id: '',
      description: '',
      opening_balance: 0
    });
  };

  const accountTypes = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];
  
  const accountTypeInfo = {
    Asset: { icon: Wallet, color: '#3b82f6', bgColor: '#eff6ff', description: 'Resources owned by the business' },
    Liability: { icon: CreditCard, color: '#ef4444', bgColor: '#fef2f2', description: 'Debts owed to others' },
    Equity: { icon: Building2, color: '#10b981', bgColor: '#ecfdf5', description: 'Owner\'s stake in the business' },
    Revenue: { icon: TrendingUp, color: '#8b5cf6', bgColor: '#f5f3ff', description: 'Income from business operations' },
    Expense: { icon: TrendingDown, color: '#f97316', bgColor: '#fff7ed', description: 'Costs of doing business' }
  };
  
  const groupedAccounts = accounts.reduce((acc, account) => {
    const type = account.account_type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(account);
    return acc;
  }, {});

  const getAccountTypeColor = (type) => {
    const colors = {
      Asset: 'bg-blue-100 text-blue-800',
      Liability: 'bg-red-100 text-red-800',
      Equity: 'bg-green-100 text-green-800',
      Revenue: 'bg-purple-100 text-purple-800',
      Expense: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="card">
          <p>Loading accounts...</p>
        </div>
      </div>
    );
  }

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.7)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      animation: 'fadeIn 0.2s ease-out'
    },
    modal: {
      background: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '580px',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      animation: 'slideUp 0.3s ease-out'
    },
    header: {
      padding: '24px 28px',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)'
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: 'white'
    },
    headerIcon: {
      width: '44px',
      height: '44px',
      borderRadius: '12px',
      background: 'rgba(255, 255, 255, 0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    closeBtn: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      border: 'none',
      background: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    body: {
      padding: '28px',
      maxHeight: 'calc(90vh - 180px)',
      overflowY: 'auto'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '20px'
    },
    formGroup: {
      marginBottom: '0'
    },
    formGroupFull: {
      gridColumn: '1 / -1'
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151'
    },
    labelIcon: {
      width: '18px',
      height: '18px',
      color: '#6b7280'
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '15px',
      transition: 'all 0.2s',
      outline: 'none',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '12px 14px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '15px',
      transition: 'all 0.2s',
      outline: 'none',
      backgroundColor: 'white',
      cursor: 'pointer',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '12px 14px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '15px',
      transition: 'all 0.2s',
      outline: 'none',
      resize: 'vertical',
      minHeight: '80px',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    accountTypeGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '10px',
      gridColumn: '1 / -1'
    },
    accountTypeBtn: {
      padding: '14px 8px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px'
    },
    footer: {
      padding: '20px 28px',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      background: '#f8fafc'
    },
    cancelBtn: {
      padding: '12px 24px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      background: 'white',
      color: '#4b5563',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    submitBtn: {
      padding: '12px 28px',
      border: 'none',
      borderRadius: '10px',
      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
      color: 'white',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    hint: {
      fontSize: '12px',
      color: '#9ca3af',
      marginTop: '6px'
    },
    required: {
      color: '#ef4444',
      marginLeft: '2px'
    },
    sectionTitle: {
      fontSize: '13px',
      fontWeight: '700',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '16px',
      gridColumn: '1 / -1',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    divider: {
      height: '1px',
      background: '#e5e7eb',
      gridColumn: '1 / -1',
      margin: '8px 0'
    }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>Chart of Accounts</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} /> New Account
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title">Filters</h3>
        </div>
        <div className="card-body">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="form-control"
            style={{ maxWidth: '300px' }}
          >
            <option value="">All Account Types</option>
            {accountTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Accounts by Type */}
      {Object.keys(groupedAccounts).map(type => (
        <div key={type} className="card mb-4">
          <div className="card-header">
            <h3 className="card-title">
              <span className={`badge ${getAccountTypeColor(type)}`} style={{ marginRight: '10px' }}>
                {type}
              </span>
              Accounts
            </h3>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Account Code</th>
                  <th>Account Name</th>
                  <th>Category</th>
                  <th>Parent Account</th>
                  <th style={{ textAlign: 'right' }}>Opening Balance</th>
                  <th style={{ textAlign: 'right' }}>Current Balance</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedAccounts[type].map(account => (
                  <tr key={account.id}>
                    <td><strong>{account.account_code}</strong></td>
                    <td>{account.account_name}</td>
                    <td>{account.account_category || '-'}</td>
                    <td>{account.parent_account_name || '-'}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(account.opening_balance)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <strong>{formatCurrency(account.current_balance)}</strong>
                    </td>
                    <td>
                      <span className={`badge ${account.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        {account.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEdit(account)}
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        {!account.is_system_account && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(account.id)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {accounts.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <FileText size={48} />
            <h3>No accounts found</h3>
            <p>Create your first account to get started</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <Plus size={20} /> Create Account
            </button>
          </div>
        </div>
      )}

      {/* Improved Modal */}
      {showModal && (
        <div style={modalStyles.overlay} onClick={closeModal}>
          <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={modalStyles.header}>
              <div style={modalStyles.headerTitle}>
                <div style={modalStyles.headerIcon}>
                  <Layers size={24} color="white" />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                    {editingAccount ? 'Edit Account' : 'Create New Account'}
                  </h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                    {editingAccount ? 'Update account details' : 'Add a new account to your chart'}
                  </p>
                </div>
              </div>
              <button 
                style={modalStyles.closeBtn} 
                onClick={closeModal}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit}>
              <div style={modalStyles.body}>
                <div style={modalStyles.formGrid}>
                  
                  {/* Account Type Selection */}
                  <div style={modalStyles.sectionTitle}>
                    <Layers size={16} />
                    Account Type
                  </div>
                  
                  <div style={modalStyles.accountTypeGrid}>
                    {accountTypes.map(type => {
                      const TypeIcon = accountTypeInfo[type].icon;
                      const isSelected = formData.account_type === type;
                      return (
                        <button
                          key={type}
                          type="button"
                          disabled={!!editingAccount}
                          onClick={() => setFormData({...formData, account_type: type})}
                          style={{
                            ...modalStyles.accountTypeBtn,
                            borderColor: isSelected ? accountTypeInfo[type].color : '#e5e7eb',
                            background: isSelected ? accountTypeInfo[type].bgColor : 'white',
                            opacity: editingAccount ? 0.6 : 1,
                            cursor: editingAccount ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <TypeIcon 
                            size={22} 
                            color={isSelected ? accountTypeInfo[type].color : '#9ca3af'} 
                          />
                          <span style={{ 
                            fontSize: '12px', 
                            fontWeight: '600',
                            color: isSelected ? accountTypeInfo[type].color : '#6b7280'
                          }}>
                            {type}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  
                  <p style={{ ...modalStyles.hint, gridColumn: '1 / -1', marginTop: '-8px' }}>
                    {accountTypeInfo[formData.account_type].description}
                  </p>

                  <div style={modalStyles.divider} />

                  {/* Basic Information */}
                  <div style={modalStyles.sectionTitle}>
                    <FileText size={16} />
                    Basic Information
                  </div>

                  {/* Account Code */}
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>
                      <Hash style={modalStyles.labelIcon} />
                      Account Code
                      <span style={modalStyles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="account_code"
                      value={formData.account_code}
                      onChange={handleChange}
                      style={{
                        ...modalStyles.input,
                        backgroundColor: editingAccount ? '#f3f4f6' : 'white'
                      }}
                      placeholder="e.g., 1000"
                      required
                      disabled={!!editingAccount}
                      onFocus={(e) => !editingAccount && (e.target.style.borderColor = '#3b82f6')}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    <p style={modalStyles.hint}>Unique identifier for the account</p>
                  </div>

                  {/* Account Name */}
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>
                      <Type style={modalStyles.labelIcon} />
                      Account Name
                      <span style={modalStyles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="account_name"
                      value={formData.account_name}
                      onChange={handleChange}
                      style={modalStyles.input}
                      placeholder="e.g., Cash on Hand"
                      required
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    <p style={modalStyles.hint}>Descriptive name for the account</p>
                  </div>

                  {/* Account Category */}
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>
                      <Layers style={modalStyles.labelIcon} />
                      Category
                    </label>
                    <input
                      type="text"
                      name="account_category"
                      value={formData.account_category}
                      onChange={handleChange}
                      style={modalStyles.input}
                      placeholder="e.g., Current Assets"
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    <p style={modalStyles.hint}>Group similar accounts together</p>
                  </div>

                  {/* Parent Account */}
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>
                      <FolderTree style={modalStyles.labelIcon} />
                      Parent Account
                    </label>
                    <select
                      name="parent_account_id"
                      value={formData.parent_account_id}
                      onChange={handleChange}
                      style={modalStyles.select}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    >
                      <option value="">None (Top Level)</option>
                      {accounts
                        .filter(acc => acc.id !== editingAccount?.id && acc.account_type === formData.account_type)
                        .map(acc => (
                          <option key={acc.id} value={acc.id}>
                            {acc.account_code} - {acc.account_name}
                          </option>
                        ))}
                    </select>
                    <p style={modalStyles.hint}>Create account hierarchy</p>
                  </div>

                  <div style={modalStyles.divider} />

                  {/* Financial Details */}
                  <div style={modalStyles.sectionTitle}>
                    <DollarSign size={16} />
                    Financial Details
                  </div>

                  {/* Opening Balance */}
                  <div style={{...modalStyles.formGroup, ...modalStyles.formGroupFull}}>
                    <label style={modalStyles.label}>
                      <DollarSign style={modalStyles.labelIcon} />
                      Opening Balance
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="opening_balance"
                      value={formData.opening_balance}
                      onChange={handleChange}
                      style={{
                        ...modalStyles.input,
                        maxWidth: '250px',
                        backgroundColor: editingAccount ? '#f3f4f6' : 'white'
                      }}
                      placeholder="0.00"
                      disabled={!!editingAccount}
                      onFocus={(e) => !editingAccount && (e.target.style.borderColor = '#3b82f6')}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    <p style={modalStyles.hint}>Starting balance when account was created</p>
                  </div>

                  <div style={modalStyles.divider} />

                  {/* Description */}
                  <div style={{...modalStyles.formGroup, ...modalStyles.formGroupFull}}>
                    <label style={modalStyles.label}>
                      <AlignLeft style={modalStyles.labelIcon} />
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      style={modalStyles.textarea}
                      placeholder="Add any notes or description for this account..."
                      rows="3"
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={modalStyles.footer}>
                <button 
                  type="button" 
                  style={modalStyles.cancelBtn}
                  onClick={closeModal}
                  onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.background = 'white'}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={modalStyles.submitBtn}
                  onMouseOver={(e) => e.target.style.opacity = '0.9'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                  {editingAccount ? (
                    <>
                      <Edit2 size={18} />
                      Update Account
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Create Account
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add keyframe animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default ChartOfAccounts;
